// TODO translate descriptions
import type { Breach, DriftAccount } from '@interfaces/driftQueue';
import type { SnapshotCardData } from '@ovComponents/3-pattern/snapshotCard';
import type {
  BatchRecapEvent,
  BatchRecapStatus,
  BatchRecapViewModel,
} from '@ovComponents/4-module/widgets/batchRecap/batchRecap.types';
import type {
  DashboardBulkTradeRun,
  DashboardOptimizer,
  DashboardPortfolioReport,
  OptimizerState,
} from '../types';
import { formatMoney, getPortfolioName, relativeTime } from '.';
import { isActionQueueReport } from './actionQueue';

/*
 * Pure mappers: FETCH_DASHBOARD_STATS results → widget view-models.
 *
 * These functions intentionally import nothing from `@apollo/client` or `react`
 * — they are presentational/data-shaping logic only, mirroring the derivations
 * in `todaysActivityTab.tsx` so v1 and v2 stay consistent. Keep them pure.
 */

// ─── Snapshot summary cards ──────────────────────────────────────────────────

export type SnapshotCardStatsInput = {
  portfolioStats: {
    urgent: number;
    moderate: number;
    actionQueue: number;
    onTrack: number;
    total: number;
    totalValue: number;
    actionQueueValue: number;
  };
  operationsStats: {
    totalActive: number;
    activeOptimizers: number;
    activeBulkRuns: number;
  };
  tradeStats: {
    initiated: number;
    ready: number;
    requested: number;
    total: number;
  };
};

export type SnapshotCardKey = 'needsAction' | 'tradePipeline' | 'opsInFlight';

export type MappedSnapshotBreakdownRow = {
  labelKey: string;
  value: string;
};

export type MappedSnapshotCard = Omit<SnapshotCardData, 'breakdown'> & {
  cardKey: SnapshotCardKey;
  onTrackCount?: number;
  breakdown?: MappedSnapshotBreakdownRow[];
};

/**
 * Build the three accent-border summary cards (Needs Action / Ops In Flight /
 * Trade Pipeline). Mirrors `summaryCards` in todaysActivityTab.tsx but outputs
 * `SnapshotCardData` with `surface: 'accentBorder'` and a `breakdown` array in
 * place of the old subtitle string.
 *
 * Labels are i18n keys under `portfolioTradingHub:snapshotCards.*` — translate
 * in the consuming tab before render.
 */
export function mapDashboardToSnapshotCards({
  portfolioStats,
  operationsStats,
  tradeStats,
}: SnapshotCardStatsInput): MappedSnapshotCard[] {
  const needsActionBreakdown: MappedSnapshotBreakdownRow[] = [];

  if (portfolioStats.urgent > 0) {
    needsActionBreakdown.push({
      labelKey: 'urgent',
      value: String(portfolioStats.urgent),
    });
  }

  if (portfolioStats.moderate > 0) {
    needsActionBreakdown.push({
      labelKey: 'moderate',
      value: String(portfolioStats.moderate),
    });
  }

  needsActionBreakdown.push({
    labelKey: 'aum',
    value: formatMoney(portfolioStats.actionQueueValue, { compact: true }),
  });

  const tradePipelineBreakdown: MappedSnapshotBreakdownRow[] = [
    { labelKey: 'staged', value: String(tradeStats.initiated) },
    { labelKey: 'approved', value: String(tradeStats.ready) },
    { labelKey: 'sent', value: String(tradeStats.requested) },
  ];

  const opsInFlightBreakdown: MappedSnapshotBreakdownRow[] = [
    {
      labelKey: 'rebalances',
      value: String(operationsStats.activeOptimizers),
    },
    { labelKey: 'batches', value: String(operationsStats.activeBulkRuns) },
  ];

  return [
    {
      cardKey: 'needsAction',
      title: 'Needs Action',
      primaryValue: String(portfolioStats.actionQueue),
      tone: portfolioStats.actionQueue > 0 ? 'negative' : 'neutral',
      surface: 'accentBorder',
      onTrackCount: portfolioStats.onTrack,
      secondaryStat: `${portfolioStats.onTrack} on track`,
      breakdown: needsActionBreakdown,
    },
    {
      cardKey: 'tradePipeline',
      title: 'Trade Pipeline',
      primaryValue: String(tradeStats.total),
      tone: tradeStats.total > 0 ? 'primary' : 'neutral',
      surface: 'accentBorder',
      secondaryStat: 'pending trades',
      breakdown: tradePipelineBreakdown,
    },
    {
      cardKey: 'opsInFlight',
      title: 'Ops In Flight',
      primaryValue: String(operationsStats.totalActive),
      tone: operationsStats.totalActive > 0 ? 'warning' : 'neutral',
      surface: 'accentBorder',
      secondaryStat: 'operations',
      breakdown: opsInFlightBreakdown,
    },
  ];
}

// ─── Drift queue rows ────────────────────────────────────────────────────────

const deriveBreaches = (r: DashboardPortfolioReport): Breach[] => {
  const breaches: Breach[] = [];
  if (r.liquidateRebalanceRequired)
    breaches.push({ reason: 'Liquidation', severity: 1 });
  if (r.transferRebalanceRequired)
    breaches.push({ reason: 'Transfer', severity: 1 });
  if (r.driftRebalanceRequired) breaches.push({ reason: 'Drift', severity: 2 });
  const unverified = r.unverifiedSubTradeRequests || 0;
  if (unverified > 0)
    breaches.push({ reason: `${unverified} Unverified`, severity: 2 });
  const pending = r.pendingSubTradeRequests || 0;
  if (pending > 0) breaches.push({ reason: `${pending} Pending`, severity: 3 });
  return breaches;
};

/**
 * Map the action-queue subset (urgent ∪ moderate, mirroring
 * todaysActivityTab.tsx) of portfolio reports into DriftQueue rows.
 *
 * `id` is the `portfolioReport.id` so the row click can deep-link into the
 * Portfolios tab (`setPinnedPortfolioDeepLink`). `custodian` is unavailable in this
 * query and renders as an em-dash.
 */
export function mapPortfolioReportsToDriftAccounts(
  reports: DashboardPortfolioReport[],
  asOf: Date = new Date(),
): DriftAccount[] {
  const asOfDate = asOf.toISOString().split('T')[0];

  return reports
    .filter((r) => isActionQueueReport(r))
    .map((report) => {
      const breaches = deriveBreaches(report);
      const marketValueCents = report.marketValueCents || 0;
      const cashCents = report.cashCents ?? 0;
      const expectedCashCents = report.expectedCashCents ?? 0;
      let cashPositionPercentage = 0;
      let assetPositionPercentage = 0;
      if (marketValueCents > 0) {
        const actualCashPct = (cashCents / marketValueCents) * 100;
        const expectedCashPct = (expectedCashCents / marketValueCents) * 100;
        cashPositionPercentage = Math.abs(actualCashPct - expectedCashPct);
        // Position-class drift needs holdings; dashboard stats omit them — show 0
        // instead of inferring from cash allocation (which read as +95% drift).
        assetPositionPercentage = 0;
      }
      const householdName =
        report.object?.householdClientGroup?.name ??
        report.object?.goal?.householdClientGroup?.name ??
        '';

      return {
        id: report.id,
        portfolioName: getPortfolioName(report),
        accountNumber: '',
        householdName,
        accountType: report.object?.account?.type ?? '',
        custodian: '—',
        portfolioValue: marketValueCents / 100,
        asOfDate,
        isStale: false,
        hasInternational: false,
        breachType: breaches.length > 1 ? 'compound' : 'single',
        breaches,
        cashPositionPercentage,
        assetPositionPercentage,
        positionDriftAvailable: false,
      };
    });
}

const RECAP_WINDOW_MS = 48 * 60 * 60 * 1000;

enum RecapTerminalState {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

const stateToStatus = (state: string): BatchRecapStatus =>
  RecapTerminalState[state as keyof typeof RecapTerminalState] ??
  (RecapTerminalState.CANCELED as OptimizerState);

const sumExchangeTotals = (run: DashboardBulkTradeRun): number =>
  (run.allTradeRequestsByExchange || []).reduce(
    (sum, ex) => sum + (ex.total || 0),
    0,
  );

type DatedEvent = { event: BatchRecapEvent; createdAt: number };

const optimizerToDatedEvent = (o: DashboardOptimizer): DatedEvent => ({
  createdAt: new Date(o.createdAt).getTime(),
  event: {
    id: o.id,
    eventType: 'OPT',
    description: `${o.numPortfolios || 0} portfolios optimized`,
    status: stateToStatus(o.state),
    timeElapsedLabel: relativeTime(o.createdAt),
    metrics: [{ label: 'portfolios', value: o.numPortfolios || 0 }],
  },
});

const bulkRunToDatedEvent = (r: DashboardBulkTradeRun): DatedEvent => {
  const completed = r.state === RecapTerminalState.COMPLETED;
  return {
    createdAt: new Date(r.createdAt).getTime(),
    event: {
      id: r.id,
      eventType: 'BATCH',
      description: `Bulk trade run ${completed ? 'completed' : r.state.toLowerCase()}`,
      status: stateToStatus(r.state),
      timeElapsedLabel: relativeTime(r.createdAt),
      metrics: [{ label: 'trades', value: sumExchangeTotals(r) }],
    },
  };
};

/**
 * Build the "Activity Recap" (BatchRecap) view-model from the optimizers +
 * bulk-run streams, scoped to the last 48h. Mirrors `yesterdayRecap` in
 * todaysActivityTab.tsx for the window + counts.
 */
export function mapDashboardToBatchRecap(
  optimizers: DashboardOptimizer[],
  bulkRuns: DashboardBulkTradeRun[],
  asOf: Date = new Date(),
): BatchRecapViewModel {
  const cutoff = asOf.getTime() - RECAP_WINDOW_MS;
  const inWindow = (createdAt: string): boolean =>
    new Date(createdAt).getTime() > cutoff;

  const completedOptimizers = optimizers.filter(
    (o) => o.state === 'COMPLETED' && inWindow(o.createdAt),
  );
  const failedOptimizers = optimizers.filter(
    (o) =>
      (o.state === 'FAILED' || o.state === 'CANCELED') && inWindow(o.createdAt),
  );
  const completedRuns = bulkRuns.filter(
    (r) => r.state === 'COMPLETED' && inWindow(r.createdAt),
  );
  const failedRuns = bulkRuns.filter(
    (r) => r.state === 'CANCELED' && inWindow(r.createdAt),
  );

  const tradesSent = completedRuns.reduce(
    (sum, run) => sum + sumExchangeTotals(run),
    0,
  );
  const portfoliosRebalanced = completedOptimizers.reduce(
    (sum, o) => sum + (o.numPortfolios || 0),
    0,
  );

  const events = [
    ...completedOptimizers.map(optimizerToDatedEvent),
    ...failedOptimizers.map(optimizerToDatedEvent),
    ...completedRuns.map(bulkRunToDatedEvent),
    ...failedRuns.map(bulkRunToDatedEvent),
  ]
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((d) => d.event);

  return {
    // Rendered inline beside the "Activity Recap" title on the v2 dashboard
    // (no "As of" prefix), so keep it short.
    asOfLabel: 'Last 48 hours',
    summary: {
      batchesRun: {
        value: completedRuns.length,
        description: 'Across 2 business days',
      },
      tradesSent: { value: tradesSent, description: 'Submitted to custodians' },
      portfoliosRebalanced: {
        value: portfoliosRebalanced,
        description: 'Across all custodians',
      },
      failedOrCanceled: {
        value: failedOptimizers.length + failedRuns.length,
        description: 'Require review',
      },
    },
    events,
  };
}
